import type { ReactNode } from 'react'

import { PageLayout } from '@components/layouts/PageLayout'

import { docsLayout } from '../components/DocsLayout'

type Swatch = { name: string; cls: string; hex: string; role?: string }

const PRIMARY: Swatch[] = [
  { name: 'primary-50', cls: 'tw:bg-primary-50', hex: '#F5F3FF' },
  { name: 'primary-100', cls: 'tw:bg-primary-100', hex: '#EDE9FE' },
  { name: 'primary-200', cls: 'tw:bg-primary-200', hex: '#DDD6FE' },
  { name: 'primary-300', cls: 'tw:bg-primary-300', hex: '#C4B5FD' },
  { name: 'primary-400', cls: 'tw:bg-primary-400', hex: '#A78BFA' },
  { name: 'primary-500', cls: 'tw:bg-primary-500', hex: '#8655F6' },
  { name: 'primary-600', cls: 'tw:bg-primary-600', hex: '#7C3AED', role: 'Accent · buttons, active nav' },
  { name: 'primary-700', cls: 'tw:bg-primary-700', hex: '#6D28D9', role: 'Accent · pressed / active text' },
  { name: 'primary-800', cls: 'tw:bg-primary-800', hex: '#5B21B6' },
  { name: 'primary-900', cls: 'tw:bg-primary-900', hex: '#4C1D95' },
]

const NEUTRALS: Swatch[] = [
  { name: 'gray-cool-50', cls: 'tw:bg-gray-cool-50', hex: '#F9F9FB', role: 'Page background' },
  { name: 'gray-cool-100', cls: 'tw:bg-gray-cool-100', hex: '#EFF1F5' },
  { name: 'gray-cool-200', cls: 'tw:bg-gray-cool-200', hex: '#DCDFEA', role: 'Borders, dividers' },
  { name: 'gray-cool-300', cls: 'tw:bg-gray-cool-300', hex: '#B9C0D4' },
  { name: 'gray-cool-400', cls: 'tw:bg-gray-cool-400', hex: '#7D89B0', role: 'Faint / placeholder text' },
  { name: 'gray-cool-500', cls: 'tw:bg-gray-cool-500', hex: '#5D6B98' },
  { name: 'gray-cool-600', cls: 'tw:bg-gray-cool-600', hex: '#4A5578', role: 'Secondary text' },
  { name: 'gray-cool-700', cls: 'tw:bg-gray-cool-700', hex: '#404968' },
  { name: 'gray-cool-800', cls: 'tw:bg-gray-cool-800', hex: '#30374F' },
  { name: 'gray-cool-900', cls: 'tw:bg-gray-cool-900', hex: '#111322', role: 'Primary text' },
]

const WARNING: Swatch[] = [
  { name: 'warning-50', cls: 'tw:bg-warning-50', hex: '#FFFAEB' },
  { name: 'warning-500', cls: 'tw:bg-warning-500', hex: '#F79009' },
  { name: 'warning-600', cls: 'tw:bg-warning-600', hex: '#DC6803', role: 'Attention / awaiting' },
  { name: 'warning-700', cls: 'tw:bg-warning-700', hex: '#B54708' },
]
const SUCCESS: Swatch[] = [
  { name: 'success-50', cls: 'tw:bg-success-50', hex: '#ECFDF3' },
  { name: 'success-500', cls: 'tw:bg-success-500', hex: '#12B76A' },
  { name: 'success-600', cls: 'tw:bg-success-600', hex: '#039855', role: 'Done / positive' },
  { name: 'success-700', cls: 'tw:bg-success-700', hex: '#027A48' },
]
const ERROR: Swatch[] = [
  { name: 'error-50', cls: 'tw:bg-error-50', hex: '#FEF3F2' },
  { name: 'error-500', cls: 'tw:bg-error-500', hex: '#F04438' },
  { name: 'error-600', cls: 'tw:bg-error-600', hex: '#D92D20', role: 'Danger / destructive' },
  { name: 'error-700', cls: 'tw:bg-error-700', hex: '#B42318' },
]

type TypeStep = { name: string; cls: string; size: string }
const TYPE_SCALE: TypeStep[] = [
  { name: 'text-xs', cls: 'tw:text-xs', size: '12 / 16px' },
  { name: 'text-sm', cls: 'tw:text-sm', size: '14 / 20px' },
  { name: 'text-base', cls: 'tw:text-base', size: '16 / 24px' },
  { name: 'text-lg', cls: 'tw:text-lg', size: '18 / 28px' },
  { name: 'text-xl', cls: 'tw:text-xl', size: '20 / 28px' },
  { name: 'text-2xl', cls: 'tw:text-2xl', size: '24 / 32px' },
]
const WEIGHTS: Array<{ name: string; cls: string }> = [
  { name: 'font-normal · 400', cls: 'tw:font-normal' },
  { name: 'font-medium · 500', cls: 'tw:font-medium' },
  { name: 'font-semibold · 600', cls: 'tw:font-semibold' },
  { name: 'font-bold · 700', cls: 'tw:font-bold' },
]
const RADII: Array<{ name: string; cls: string; use: string }> = [
  { name: 'rounded-lg · 8px', cls: 'tw:rounded-lg', use: 'Buttons, controls, pills' },
  { name: 'rounded-xl · 12px', cls: 'tw:rounded-xl', use: 'Cards, sections, menus' },
  { name: 'rounded-full', cls: 'tw:rounded-full', use: 'Count badges, avatars' },
]

function DocSection({ title, intro, children }: { title: string; intro?: string; children: ReactNode }) {
  return (
    <section className="tw:flex tw:flex-col tw:gap-4">
      <div>
        <h2 className="tw:text-lg tw:font-bold tw:text-gray-cool-900">{title}</h2>
        {intro && <p className="tw:mt-1 tw:max-w-prose tw:text-sm tw:text-gray-cool-600">{intro}</p>}
      </div>
      {children}
    </section>
  )
}

function SwatchRow({ label, swatches }: { label: string; swatches: Swatch[] }) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <h3 className="tw:text-sm tw:font-semibold tw:text-gray-cool-700">{label}</h3>
      <div className="tw:flex tw:flex-wrap tw:gap-3">
        {swatches.map((s) => (
          <div key={s.name} className="tw:w-32">
            <div className={`tw:h-14 tw:w-full tw:rounded-lg tw:border tw:border-gray-cool-200 ${s.cls}`} />
            <div className="tw:mt-1.5 tw:font-mono tw:text-xs tw:text-gray-cool-900">{s.name}</div>
            <div className="tw:font-mono tw:text-xs tw:text-gray-cool-400">{s.hex}</div>
            {s.role && <div className="tw:mt-0.5 tw:text-xs tw:text-gray-cool-500">{s.role}</div>}
          </div>
        ))}
      </div>
    </div>
  )
}

const Foundations = () => {
  return (
    <>
      <PageLayout.Header icon="bank" heading="Foundations" />
      <PageLayout.Body>
        <div className="tw:mx-auto tw:flex tw:max-w-5xl tw:flex-col tw:gap-10 tw:pb-16">
          <header className="tw:flex tw:flex-col tw:gap-2 tw:border-b tw:border-gray-cool-200 tw:pb-6">
            <h1 className="tw:max-w-2xl tw:text-2xl tw:font-bold tw:text-gray-cool-900">Design tokens</h1>
            <p className="tw:max-w-prose tw:text-sm tw:text-gray-cool-600">
              The raw design decisions everything else references — colour, type and radius — sourced from
              config/tailwind.config.js. Each swatch renders with its real token class, so what you see is the
              live value; the hex beside it is for Figma.
            </p>
            <div className="tw:mt-1 tw:flex tw:flex-wrap tw:gap-2 tw:text-xs">
              {['source · tailwind.config.js', 'primary · Tailwind v4 violet', 'tier · primitive'].map((tag) => (
                <span
                  key={tag}
                  className="tw:rounded-md tw:border tw:border-gray-cool-100 tw:bg-gray-cool-50 tw:px-2 tw:py-1 tw:font-mono tw:text-gray-cool-600"
                >
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <DocSection
            title="Colour"
            intro="Violet is the single brand accent; gray-cool is the neutral family; amber / green / red carry status meaning only — never decoration. Reference the token, not the raw hex, so a re-theme is one change."
          >
            <SwatchRow label="Primary (accent)" swatches={PRIMARY} />
            <SwatchRow label="Neutrals — gray-cool" swatches={NEUTRALS} />
            <div className="tw:grid tw:gap-6 tw:sm:grid-cols-3">
              <SwatchRow label="Warning — attention" swatches={WARNING} />
              <SwatchRow label="Success — done" swatches={SUCCESS} />
              <SwatchRow label="Error — danger" swatches={ERROR} />
            </div>
          </DocSection>

          <DocSection
            title="Typography"
            intro="One typeface across the UI — Inter, via tw:font-sans. The scale below is rendered live; sizes are Tailwind defaults."
          >
            <div className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-cool-100 tw:rounded-xl tw:border tw:border-gray-cool-200 tw:bg-white">
              {TYPE_SCALE.map((t) => (
                <div key={t.name} className="tw:flex tw:flex-wrap tw:items-baseline tw:gap-4 tw:px-4 tw:py-3">
                  <span className={`${t.cls} tw:flex-1 tw:font-medium tw:text-gray-cool-900`}>
                    North Ward Volunteers
                  </span>
                  <span className="tw:font-mono tw:text-xs tw:text-gray-cool-500">{t.name}</span>
                  <span className="tw:w-24 tw:text-right tw:font-mono tw:text-xs tw:text-gray-cool-400">{t.size}</span>
                </div>
              ))}
            </div>
            <div className="tw:flex tw:flex-wrap tw:gap-6">
              {WEIGHTS.map((w) => (
                <div key={w.name} className="tw:flex tw:flex-col tw:gap-1">
                  <span className={`${w.cls} tw:text-lg tw:text-gray-cool-900`}>Aa</span>
                  <span className="tw:font-mono tw:text-xs tw:text-gray-cool-500">{w.name}</span>
                </div>
              ))}
            </div>
          </DocSection>

          <DocSection
            title="Radius"
            intro="Cards use the larger radius; the controls inside them use the smaller one."
          >
            <div className="tw:flex tw:flex-wrap tw:gap-4">
              {RADII.map((r) => (
                <div key={r.name} className="tw:flex tw:w-40 tw:flex-col tw:gap-2">
                  <div className={`tw:h-16 tw:w-full tw:border tw:border-primary-200 tw:bg-primary-50 ${r.cls}`} />
                  <div>
                    <div className="tw:font-mono tw:text-xs tw:text-gray-cool-900">{r.name}</div>
                    <div className="tw:text-xs tw:text-gray-cool-500">{r.use}</div>
                  </div>
                </div>
              ))}
            </div>
          </DocSection>

          <p className="tw:border-t tw:border-gray-cool-200 tw:pt-5 tw:text-sm tw:text-gray-cool-400">
            These are primitive tokens. The semantic layer (color-action-primary, color-text-default …) and
            the full tables live in the editable companion under docs/design-system/. Values marked in that
            doc as “to confirm” — chiefly the semantic token names — are still proposals for design and
            engineering to ratify.
          </p>
        </div>
      </PageLayout.Body>
    </>
  )
}

Foundations.layout = docsLayout

export default Foundations
